import _defaultColors, { createColors } from 'picocolors';
import { write } from './write.js';
import type { Formatter } from 'picocolors/types.js';

export type { Formatter };

export const ansiEscape = '\x1b[';

let isAnsiDisabled =
  // Include the default color support detection from picocolors, including NO_COLOR env
  !_defaultColors.isColorSupported ||
  process.argv.includes('--no-color') ||
  process.argv.includes('-n') ||
  // using @ms-cloudpack/environment creates headaches here since this package has to compile to CJS
  // eslint-disable-next-line no-restricted-properties
  !!process.env.JEST_WORKER_ID;

/** Enabled color functions */
const colors = createColors(true);

/**
 * DO NOT call this except in `TaskReporter`! (otherwise updates won't be respected everywhere)
 *
 * Change whether log functions using ANSI escape codes (such as colors and progress) should be disabled.
 * If disabled, color functions will just return the text passed in.
 */
export function setAnsiDisabled(disabled: boolean): void {
  isAnsiDisabled = disabled;
}

export function stripAnsi(text: string): string {
  // eslint-disable-next-line no-control-regex -- supposed to match a control character
  return text.replace(/\x1b\[[\d;]+(m|[AKB]\b)/g, '');
}

/**
 * Count the number of visible terminal lines a string occupies, accounting for ANSI escape
 * sequences (which have zero width) and line wrapping at the given column width.
 *
 * NOTE: This does not account for tab characters, because they're relatively uncommon in
 * terminal output and not directly used by Cloudpack. Also doesn't account for wide characters,
 * but the heuristic is probably good enough for most cases.
 */
export function visibleLineCount(text: string, columns: number): number {
  // If the raw length fits in one line and has no newlines, visible length must also fit
  // (ignoring tabs as mentioned above, and assuming no ANSI moves in regular logs)
  if (text.length <= columns && !text.includes('\n')) {
    return 1;
  }

  let totalLines = 0;
  let col = 0;

  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 0x1b && text.charCodeAt(i + 1) === /* [ */ 0x5b) {
      // Skip ANSI escape sequence: \x1b[ + params (digits/semicolons) + terminator
      for (i += 2; i < text.length; i++) {
        const ch = text.charCodeAt(i);
        if ((ch < /* 0 */ 0x30 || ch > /* 9 */ 0x39) && ch !== /* ; */ 0x3b) {
          break;
        }
      }
    } else if (text.charCodeAt(i) === /* \n */ 0x0a) {
      // Newline — finish the current line and start a new one
      totalLines++;
      col = 0;
    } else {
      col++;
      if (col === columns) {
        // Wrap to the next line
        totalLines++;
        col = 0;
      }
    }
  }

  // Count the last line if it has content, if there were no lines at all (empty string),
  // or if the text ended with a newline (cursor moved to a new empty line).
  // Don't count it when the text ended at an exact column boundary (deferred wrap).
  if (col > 0 || totalLines === 0 || text.charCodeAt(text.length - 1) === /* \n */ 0x0a) {
    totalLines++;
  }
  return totalLines;
}

/*
 * Moves the cursor up N lines.
 */
export function moveUpLines(count = 1): void {
  if (!isAnsiDisabled && count) {
    write(`${ansiEscape}${count}A`);
  }
}

/*
 * Moves the cursor down N lines.
 */
export function moveDownLines(count = 1): void {
  if (!isAnsiDisabled && count) {
    write(`${ansiEscape}${count}B`);
  }
}

/**
 * Writes a string with rgb color gradient, defaulting from red to orange.
 */
export function gradient(
  text: string,
  from: readonly [number, number, number] = [255, 0, 0],
  to: readonly [number, number, number] = [255, 165, 0],
): string {
  if (isAnsiDisabled || !text) {
    return text;
  }

  if (text.length === 1) {
    // don't divide by 0, just use the start color
    return gradientChar(text, from[0], from[1], from[2]);
  }

  let result = '';

  const stepR = (to[0] - from[0]) / (text.length - 1);
  const stepG = (to[1] - from[1]) / (text.length - 1);
  const stepB = (to[2] - from[2]) / (text.length - 1);

  for (let i = 0; i < text.length; i++) {
    // Compute the rgb values for the current character where the first color should match "from" and the last should
    // match "to", and values in between should be interpolated.
    const r = Math.floor(from[0] + stepR * i);
    const g = Math.floor(from[1] + stepG * i);
    const b = Math.floor(from[2] + stepB * i);

    result += gradientChar(text[i], r, g, b);
  }

  return result;
}

function gradientChar(text: string, r: number, g: number, b: number): string {
  // https://en.wikipedia.org/wiki/ANSI_escape_code#24-bit
  return `${ansiEscape}38;2;${r};${g};${b}m${text}${ansiEscape}39m`;
}

/*
 * Clears the current line.
 */
export function clearLine(): void {
  if (!isAnsiDisabled) {
    write(`${ansiEscape}2K`);
  }
}

/*
 * Clears the entire screen including scrollback buffer and moves the cursor to the top left.
 */
export function clearScreen(): void {
  if (!isAnsiDisabled) {
    write(`${ansiEscape}2J${ansiEscape}3J${ansiEscape}H`);
  }
}

/**
 * Wrap a picocolors format function with an `isAnsiDisabled` check.
 *
 * See https://en.wikipedia.org/wiki/ANSI_escape_code#3-bit_and_4-bit for examples of
 * actual colors corresponding to ANSI escapes in various terminals.
 */
function createColorFunction(formatter: Formatter): Formatter {
  return (text) => (text === undefined ? '' : isAnsiDisabled ? '' + text : formatter(text));
}

export const red = createColorFunction(colors.red);

export const lightRed = createColorFunction(colors.redBright);

export const green = createColorFunction(colors.green);

export const lightGreen = createColorFunction(colors.greenBright);

export const yellow = createColorFunction(colors.yellow);

export const lightYellow = createColorFunction(colors.yellowBright);

export const blue = createColorFunction(colors.blue);

export const lightBlue = createColorFunction(colors.blueBright);

export const magenta = createColorFunction(colors.magenta);

export const lightMagenta = createColorFunction(colors.magentaBright);

export const cyan = createColorFunction(colors.cyan);

export const lightCyan = createColorFunction(colors.cyanBright);

/** This uses the "white" code which is usually light grey */
export const white = createColorFunction(colors.white);

/** This uses the "bright white" code which is is usually white */
export const lightWhite = createColorFunction(colors.whiteBright);

export const black = createColorFunction(colors.black);

/** This uses the "bright black" code which is usually dark grey */
export const darkGrey = createColorFunction(colors.gray);

/** This uses the "white" code which is usually light grey */
export const lightGrey = createColorFunction(colors.white);

export const bold = createColorFunction(colors.bold);
